import { useState, useEffect, useRef, useCallback } from "react";

export function useChatMessages(messages, loadOlderMessages, hasMoreMessages) {
  const [chatMessages, setChatMessages] = useState(messages);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const chatContainerRef = useRef(null);
  const prevLengthRef = useRef(0);
  const scrollRestoreRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Sync chatMessages with parent messages prop
  useEffect(() => {
    const container = chatContainerRef.current;
    if (
      container &&
      chatMessages.length > 0 &&
      messages.length > chatMessages.length &&
      messages[0]?._id !== chatMessages[0]?._id
    ) {
      // Older messages prepended — snapshot scroll for restoration
      scrollRestoreRef.current = { prevScrollHeight: container.scrollHeight };
    }
    setChatMessages(messages);
  }, [messages]);

  // Handle scroll: restore position on prepend, scroll to bottom on append/initial
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    if (scrollRestoreRef.current) {
      // Older messages were prepended — preserve scroll position
      const { prevScrollHeight } = scrollRestoreRef.current;
      container.scrollTop = container.scrollHeight - prevScrollHeight;
      scrollRestoreRef.current = null;
      isLoadingRef.current = false;
      setIsLoadingOlder(false);
    } else if (prevLengthRef.current !== chatMessages.length) {
      // Initial load or new message appended — scroll to bottom
      container.scrollTop = container.scrollHeight;
    }

    prevLengthRef.current = chatMessages.length;
  }, [chatMessages]);

  // Scroll-to-top detection for loading older messages
  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container || isLoadingRef.current || !hasMoreMessages) return;

    if (container.scrollTop <= 50) {
      isLoadingRef.current = true;
      setIsLoadingOlder(true);
      loadOlderMessages();
    }
  }, [loadOlderMessages, hasMoreMessages]);

  // Attach scroll listener
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return { chatMessages, setChatMessages, chatContainerRef, isLoadingOlder };
}
